import { makeAutoObservable } from 'mobx'
import RootStore from './root'
import { HttpReq } from '../common/utils/http'
import { PagingSubStore, SingleSubStore } from './subStore'
import {
  AccountHistory,
  CommunityList,
  FavoriteResult,
  HistoryList,
  NoticeList,
  ProductList,
  QnaDetailList,
  QnaList,
  ReqSettlement,
  ResAssetSummary,
  ResDepositAccount,
  ResDisposalDetail,
  ResDisposalVote,
  ResNoticeDetail,
  ResProductDetail,
  ResPurchaseDetailData,
  ResultRegisterContents,
  ResultRegisterQna,
  ResultWithdrawalBalance,
  ResUserInfo,
  ResWithdrawalAccount,
  UserInfo,
} from '../common/constants/types'

export class MyStore {
  rootStore: RootStore
  /* 메인 자산 summary */
  assetSummaryStore = new SingleSubStore<ResAssetSummary>(`/purchase/summary`)

  /* 총 보유 자산 */
  assetHoldPagingStore = new PagingSubStore<ProductList>('/purchase/products') // 소유중인 리스트
  assetFinishPagingStore = new PagingSubStore<ProductList>(`/purchase/products?isFinished=true`) //매각 완료 리스트
  assetHistoryPagingStore = new PagingSubStore<HistoryList>('/purchase') //  거래내역 리스트

  /* 작품 상세 */
  purchasePagingStore = new PagingSubStore<HistoryList>(`/purchase`) // 거래내역
  purchaseDetailStore = new SingleSubStore<ResPurchaseDetailData>( // 작품 정보
    (param?: Record<string, any>) => `/purchase/products/${param?.productId}`,
  )
  productDetailStore = new SingleSubStore<ResProductDetail>( // 작품 상세 정보
    (param?: Record<string, any>) => `/product/${param?.productId}`,
  )
  productDisposalStore = new SingleSubStore<ResDisposalDetail>( // 작품 매각 정보
    (param?: Record<string, any>) => `/product/vote/${param?.voteId}`,
  )
  communityPagingStore = new PagingSubStore<CommunityList>( // 작품 커뮤니티
    (param?: Record<string, any>) => `/product/${param?.productId}/post`,
  )

  /* 예치금 */
  accountPagingStore = new PagingSubStore<AccountHistory>(`/cash/transaction`) // 입출금 내역
  depositAccountStore = new SingleSubStore<ResDepositAccount>(`/user/current/depositAccount`) // 충전 계좌 조회
  withdrawalAccountStore = new SingleSubStore<ResWithdrawalAccount>(`/user/current/withdrawalAccount`) // 출금 계좌 조회

  /* 공지사항 */
  noticePagingStore = new PagingSubStore<NoticeList>(`/notice`) // 공지사항 리스트
  noticeDetailStore = new SingleSubStore<ResNoticeDetail>( // 상세 정보
    (param?: Record<string, any>) => `/notice/${param?.notificationId}`,
  )

  /* 문의사항 */
  qnaPagingStore = new PagingSubStore<QnaList>(`/user/qna`) // 문의사항 리스트
  qnaDetailPagingStore = new PagingSubStore<QnaDetailList>( // 문의사항 상세 페이지
    (param?: Record<string, any>) => `/user/qna/${param?.qnaId}/chats`,
  )

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore
    makeAutoObservable(this)
  }

  /* 임시 - 예치금 출금 */
  withdrawalBalance = async (data: { balance: number }): Promise<ResultWithdrawalBalance | null> => {
    return await HttpReq<ResultWithdrawalBalance>({
      method: 'POST',
      url: `/user/withdrawal`,
      data: data,
    })
  }

  /* 문의사항 생성 */
  registerQna = async (data: { title: string; content: string }): Promise<ResultRegisterQna | null> => {
    return await HttpReq<ResultRegisterQna>({
      method: 'POST',
      url: `/user/qna`,
      data: data,
    })
  }

  /* 문의사항 추가 문의 */
  registerQnaChats = async (qnaId: number, content: string): Promise<ResultRegisterQna | null> => {
    return await HttpReq<ResultRegisterQna>({
      method: 'POST',
      url: `/user/qna/${qnaId}/chats`,
      data: { content: content },
    })
  }

  /* 유저 정보 수정 */
  modifyUserInfo = async (data: UserInfo): Promise<ResUserInfo | null> => {
    const updateUserInfo = await HttpReq<ResUserInfo>({
      method: 'PUT',
      url: `/user`,
      data: data,
    })
    if (updateUserInfo != null) {
      this.rootStore.authStore.updateUserInfo(updateUserInfo)
    } else {
      return null
    }
    return updateUserInfo
  }

  /* 커뮤니티 댓글 작성 */
  registerContents = async (productId: number, contents: string): Promise<ResultRegisterContents | null> => {
    return await HttpReq<ResultRegisterContents>({
      method: 'POST',
      url: `/product/${Number(productId)}/post`,
      data: { contents: contents },
    })
  }

  /* 커뮤니티 좋아요 기능 */
  likeContent = async (productId: number, communityId: number): Promise<boolean | null> => {
    return await HttpReq<boolean>({
      method: 'POST',
      url: `/product/${productId}/post/${communityId}/like`,
    })
  }

  /* 커뮤니티 댓글 삭제 */
  deleteContent = async (productId: number, communityId: number): Promise<boolean | null> => {
    const deleteResult = await HttpReq<boolean>({
      method: 'DELETE',
      url: `/product/${productId}/post/${communityId}`,
    })
    if (!deleteResult) {
      return null
    }
    return deleteResult
  }

  /* 매각 투표 */
  disposalVote = async (productId: number, type: 'ACCEPT' | 'REJECT'): Promise<ResDisposalVote | null> => {
    const voteResult = await HttpReq<ResDisposalVote>({
      method: 'POST',
      url: `/product/vote/${productId}`,
      data: { type: type },
    })
    if (voteResult == null) {
      return null
    }
    return voteResult
  }

  /* 정산하기 */
  settlement = async (voteId: number, data: ReqSettlement): Promise<ReqSettlement | null> => {
    const settlement = await HttpReq<ReqSettlement>({
      method: 'POST',
      url: `/product/vote/${voteId}/settlement`,
      data: data,
    })
    return settlement
  }

  /* 작품 알림 설정하기 */
  favoriteProduct = async (productId: number) => {
    const favoriteResult = await HttpReq<FavoriteResult>({
      method: 'POST',
      url: `/product/${productId}/favorite`,
    })
    return favoriteResult?.favorite
  }

  // 판매율
  get saleRate() {
    if (this.productDetailStore.content != null) {
      const detailContent = this.productDetailStore.content
      const saleRate =
        ((detailContent?.tokenCount - detailContent.activeProductSale?.remainCount) / detailContent?.tokenCount) * 100
      return saleRate
    } else {
      return 0
    }
  }

  // 투표율
  get voteRateDetail() {
    if (this.productDetailStore.content != null) {
      const detailContent = this.productDetailStore.content
      const voteRate = (detailContent?.voteCount / detailContent.activeProductVote?.suppliedTokenCount) * 100
      return voteRate
    } else {
      return 0
    }
  }

  // 최초 작품 금액
  get initSalePriceDetail() {
    if (this.productDetailStore.content != null) {
      const detailContent = this.productDetailStore.content
      const initSalePrice = detailContent.activeProductSale?.cost * detailContent?.tokenCount
      return initSalePrice
    } else {
      return 0
    }
  }

  // 매각 수익율
  get priceRate() {
    if (this.productDetailStore.content != null && this.initSalePriceDetail != null) {
      const detailContent = this.productDetailStore.content
      const priceRate =
        ((detailContent.sellHistory?.salePrice - this.initSalePriceDetail) / this.initSalePriceDetail) * 100
      return priceRate
    } else {
      return 0
    }
  }

  // 변동 자산률 - 보유 자산 - 작품 상세 페이지
  get assetChangeRate() {
    if (this.productDetailStore.content != null) {
      const averageCost = Number(this.productDetailStore.content?.averageCost)
      const assetChangeRate = ((this.productDetailStore.content?.currentCost - averageCost) / averageCost) * 100
      return assetChangeRate
    } else {
      return 0
    }
  }

  // 변동 자산률 - 메인 summary
  get balanceChangeRate() {
    if (this.assetSummaryStore.content != null) {
      const purchasedBalance = Number(this.assetSummaryStore.content?.purchasedBalance)
      const currentBalance = Number(this.assetSummaryStore.content?.currentBalance)
      const balanceChangeRate = ((currentBalance - purchasedBalance) / purchasedBalance) * 100
      return balanceChangeRate
    } else {
      return 0
    }
  }

  // 사용 가능 금액
  get tradableBalance() {
    if (this.assetSummaryStore.content != null) {
      const cashBalance = Number(this.assetSummaryStore.content?.cashBalance) // 예치금
      const lockedBalance = Number(this.assetSummaryStore.content?.lockedBalance) // 마켓 대기 금액
      const tradableBalance = cashBalance - lockedBalance
      return tradableBalance
    } else {
      return 0
    }
  }

  // 예상 수익률
  get expectedCostRate() {
    if (this.productDisposalStore.content != null) {
      const detailData = this.productDisposalStore.content
      const expectedCostRate =
        ((detailData.revenue?.sellCost - detailData.revenue?.averageCost) / detailData.revenue?.averageCost) * 100
      return expectedCostRate
    } else {
      return 0
    }
  }

  // 예상 수익금
  get expectedCost() {
    if (this.productDisposalStore.content != null) {
      const detailData = this.productDisposalStore.content
      const expectedCost = detailData.revenue?.sellCost * detailData.revenue?.count
      return expectedCost
    } else {
      return 0
    }
  }

  // 투표율
  get voteRateDisposal() {
    if (this.productDisposalStore.content != null) {
      const detailData = this.productDisposalStore.content
      const voteRate = (detailData?.voteCount / detailData?.suppliedTokenCount) * 100
      return voteRate
    } else {
      return 0
    }
  }

  // 투표 행사수량
  get totalVoteCount() {
    if (this.productDisposalStore.content != null) {
      const detailData = this.productDisposalStore.content
      const totalVoteCount = detailData?.approveCount + detailData?.rejectCount
      return totalVoteCount
    } else {
      return 0
    }
  }

  // 전체 투표 대비 찬성 투표율
  get approveVoteRate() {
    if (this.productDisposalStore.content != null) {
      const detailData = this.productDisposalStore.content
      const approveVoteRate = (detailData?.approveCount / this.totalVoteCount) * 100
      return approveVoteRate
    } else {
      return 0
    }
  }

  // 전체 투표 대비 반대 투표율
  get rejectVoteRate() {
    if (this.productDisposalStore.content != null) {
      const detailData = this.productDisposalStore.content
      const rejectVoteRate = (detailData?.rejectCount / this.totalVoteCount) * 100
      return rejectVoteRate
    } else {
      return 0
    }
  }

  // 작품 판매 가격
  get initSalePriceDisposal() {
    if (this.productDisposalStore.content != null) {
      const detailData = this.productDisposalStore.content
      const initSalePrice = detailData.product?.activeProductSale.cost * detailData.product?.tokenCount
      return initSalePrice
    } else {
      return 0
    }
  }
}
